import { PageHeader, Text } from '@lawpilots/design-system'
import { Box, Stack } from '@mui/material'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { LoadingPanel, PageLayout } from '@/common/components'
import { FEATURES } from '@/features.config'
import { getUserDisplayName } from '@/features/auth'
import { usePageVisited, useViewport } from '@/hooks'
import { CertificateDto, useGetCurrentUserCertificatesQuery } from '@/services/api/tenant/certificates'
import { RootState, useTypedSelector } from '@/store'
import { LearnerEmptyState } from '../../components/LearnerEmptyState'
import { LearnerSection } from '../../components/LearnerSection'
import { CardGridStyles } from '../../components/LearnerPage'
import { MyCertificateCard } from '../components/MyCertificateCard'
import { CertificateViewModal } from '../components/Modals/CertificateViewModal'
import { useCertificateDownload } from '../hooks/useCertificateDownload'
import { groupCertificatesByYear } from '../utils/groupCertificatesByYear'

const Certificates = (): JSX.Element => {
  const { t } = useTranslation('general')
  const accessedTenant = useTypedSelector((state: RootState) => state.authSlice.tenant)
  const { data: certificates = [], isLoading } = useGetCurrentUserCertificatesQuery({ tenantId: accessedTenant.id })
  const currentUser = useTypedSelector((state: RootState) => state.authSlice.user)
  const userFullName = getUserDisplayName(currentUser)
  const [openedCertificate, setOpenedCertificate] = useState<CertificateDto | null>(null)
  const downloadCertificate = useCertificateDownload(userFullName)
  const viewport = useViewport()
  usePageVisited('My Certificates')

  const currentYear = new Date().getFullYear()
  const yearGroups = useMemo(() => groupCertificatesByYear(certificates, currentYear), [certificates, currentYear])

  // The compact card is mobile only: the design puts tablet on the full card (Figma 2018:13296).
  const compact = viewport === 'mobile'

  return (
    <PageLayout feature={FEATURES.MY_CERTIFICATES}>
      <Stack spacing={1}>
        <PageHeader title={t('my_certificates_title')} />
        {/* Empty branch lives inside the loading wrapper so an empty array mid-fetch cannot flash
            the empty state (FS-272). */}
        <LoadingPanel loading={isLoading}>
          {yearGroups.length === 0 ? (
            <LearnerEmptyState
              title={t('my_certificates_empty_title')}
              description={t('my_certificates_empty_description')}
            />
          ) : (
            <Stack spacing={6}>
              {yearGroups.map(({ year, certificates: earned }) => (
                <LearnerSection key={year} label={String(year)}>
                  {earned.length === 0 ? (
                    <Text variant="body-reading" color="subtle">
                      {t(year === currentYear ? 'my_certificates_year_empty_current' : 'my_certificates_year_empty')}
                    </Text>
                  ) : (
                    <Box sx={CardGridStyles}>
                      {earned.map((certificate) => (
                        <MyCertificateCard
                          key={certificate.id}
                          certificate={certificate}
                          compact={compact}
                          onView={setOpenedCertificate}
                          onDownload={downloadCertificate}
                        />
                      ))}
                    </Box>
                  )}
                </LearnerSection>
              ))}
            </Stack>
          )}
        </LoadingPanel>
      </Stack>
      {openedCertificate && (
        <CertificateViewModal
          open
          certificate={openedCertificate}
          onCloseAction={() => setOpenedCertificate(null)}
          onDownload={downloadCertificate}
        />
      )}
    </PageLayout>
  )
}

export default Certificates
